import { CommandExecutionContext, SelfContainedCommand } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { googleDriveAdapter } from '../../../services/integrations/adapters/GoogleDriveAdapter';
import { resolveGoogleDriveIntegration } from '../../../services/integrations/utils/integrationResolver';

const log = createFileLogger('GOOGLE_DOC_WRITE');

export const googleDocWriteCommand: SelfContainedCommand = {
  id: 'google_doc_write',
  description: `Write or update content in a Google Doc

Executes batch operations on a Google Doc using the Google Docs API.

**Parameters:**
- documentId: Google Doc ID (from URL)
- operations: Array of write operations (insertText, deleteContentRange, replaceAllText, etc.)
- integrationId (optional): Integration ID (auto-detected if omitted)

**Common Operations:**
1. Insert text at index:
   { "insertText": { "text": "Hello", "location": { "index": 1 } } }

2. Delete content range:
   { "deleteContentRange": { "range": { "startIndex": 1, "endIndex": 10 } } }

3. Replace all text:
   { "replaceAllText": { "containsText": { "text": "old", "matchCase": true }, "replaceText": "new" } }

4. Update paragraph style:
   { "updateParagraphStyle": { "range": { "startIndex": 1, "endIndex": 10 }, "paragraphStyle": { "alignment": "CENTER" }, "fields": "alignment" } }

**Note:** Operations are executed in order. Index positions change after insertions/deletions.`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'documentId',
        type: 'string',
        description: 'Google Doc ID from Drive file URL',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          return null;
        }
      },
      {
        name: 'operations',
        type: 'array',
        description: 'Array of Google Docs API batch update requests (objects with operation type and parameters)',
        required: true,
        validator: (value: any) => {
          if (!Array.isArray(value)) return 'Operations must be an array';
          if (value.length === 0) return 'At least one operation is required';
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Integration ID (auto-detected if omitted)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          documentId: '1ABC123xyz',
          operations: [
            { insertText: { text: 'Hello World', location: { index: 1 } } }
          ]
        },
        expectedOutput: createSuccessResult('Successfully updated Google Doc', {
          document_id: '1ABC123xyz',
          success: true
        }),
        description: 'Insert text into a Google Doc',
        context: 'When you need to add content to a Google Doc'
      }
    ],
    bad: [
      {
        input: { documentId: '1ABC123xyz', operations: [] },
        error: 'At least one operation is required',
        why: 'Empty operations array',
        fix: 'Provide at least one valid operation'
      }
    ]
  },
  
  performance: {
    expectedDuration: 3000,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let { documentId, operations, integrationId } = params;
      
      if (!documentId) {
        return createErrorResult('Missing document ID', 'documentId parameter is required', 'validation');
      }
      
      // Check if documentId is an llmId (entity reference) and resolve to actual Drive ID
      if (documentId.includes('_')) {
        log.log('[google_doc_write] 🔍 Detected llmId, resolving to driveFileId:', documentId);
        try {
          const useEntityStore = (await import('../../../stores/entityStore')).default;
          const allEntities = useEntityStore.getState().entities;
          log.log('[google_doc_write] 🔍 EntityStore has', allEntities.length, 'entities');
          
          const entity = allEntities.find((e: any) => e.llmId === documentId);
          log.log('[google_doc_write] 🔍 Found entity:', { 
            found: !!entity, 
            hasLlmId: entity?.llmId,
            hasSkeleton: !!entity?.skeleton,
            hasDriveFileId: !!entity?.skeleton?.driveFileId,
            driveFileId: entity?.skeleton?.driveFileId,
            entityType: entity?.entityType
          });
          
          if (entity && entity.skeleton?.driveFileId) {
            log.log('[google_doc_write] ✅ Resolved llmId to driveFileId:', { llmId: documentId, driveFileId: entity.skeleton.driveFileId });
            documentId = entity.skeleton.driveFileId;
          } else {
            log.warn('[google_doc_write] ❌ Could not resolve llmId to driveFileId:', { 
              documentId, 
              hasEntity: !!entity,
              hasSkeleton: !!entity?.skeleton,
              skeletonKeys: entity?.skeleton ? Object.keys(entity.skeleton) : []
            });
          }
        } catch (error) {
          log.error('[google_doc_write] ❌ Error resolving llmId:', error);
        }
      } else {
        log.log('[google_doc_write] 💡 documentId does not look like llmId (no underscore):', documentId);
      }
      
      if (!operations || !Array.isArray(operations) || operations.length === 0) {
        return createErrorResult(
          'Invalid operations',
          'operations must be a non-empty array',
          'validation'
        );
      }
      
      log.log('[google_doc_write] Writing to Google Doc:', {
        documentId,
        operationCount: operations.length,
        hasIntegrationId: !!integrationId
      });
      
      // Resolve integration ID if not provided
      let resolvedIntegrationId = integrationId;
      if (!resolvedIntegrationId) {
        try {
          resolvedIntegrationId = await resolveGoogleDriveIntegration(context.userId);
          log.log('[google_doc_write] Auto-resolved integration ID:', resolvedIntegrationId);
        } catch (error) {
          return createErrorResult(
            'No Google Drive integration found',
            error instanceof Error ? error.message : 'Please connect Google Drive in Settings → Integrations',
            'validation'
          );
        }
      }
      
      // Write to document via adapter
      const result = await googleDriveAdapter.writeGoogleDoc(
        resolvedIntegrationId,
        documentId,
        operations
      );
      
      log.log('[google_doc_write] Successfully wrote to document');
      
      return createSuccessResult(
        result.message || `Successfully updated Google Doc with ${operations.length} operation(s)`,
        {
          document_id: result.document_id,
          success: result.success,
          operations_executed: operations.length,
          replies: result.replies
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'google_doc_write'
          } as any
        }
      );
      
    } catch (error) {
      log.error('[google_doc_write] Error writing to Google Doc:', error);
      return createErrorResult(
        'Failed to write to Google Doc',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

